import { useState } from "react";
import {
  Download,
  FileText,
  Globe,
  Code,
  Table,
  ChevronDown,
  Check,
  Loader2,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@asm/ui";
import {
  exportDetailedReport,
  type DetailedExportFormat,
} from "@/lib/export-detailed-report";
import type { Scan, ScanResult } from "@/lib/api";

interface AdvancedExportMenuProps {
  scan: Scan;
  results: ScanResult;
  className?: string;
}

const EXPORT_OPTIONS: Array<{
  format: DetailedExportFormat;
  label: string;
  description: string;
  icon: typeof FileText;
}> = [
  {
    format: "pdf",
    label: "PDF Report",
    description: "Full report with findings table",
    icon: FileText,
  },
  {
    format: "html",
    label: "HTML Report",
    description: "Complete technical report",
    icon: Globe,
  },
  {
    format: "json",
    label: "JSON Data",
    description: "Raw data including all modules",
    icon: Code,
  },
  {
    format: "csv",
    label: "CSV Findings",
    description: "Findings table for spreadsheets",
    icon: Table,
  },
];

export function AdvancedExportMenu({
  scan,
  results,
  className = "",
}: AdvancedExportMenuProps) {
  const [exporting, setExporting] = useState<DetailedExportFormat | null>(null);
  const [lastExported, setLastExported] = useState<DetailedExportFormat | null>(null);

  const handleExport = async (format: DetailedExportFormat) => {
    setExporting(format);

    // Small delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      exportDetailedReport(scan, results, format);
      setLastExported(format);

      // Clear the "last exported" indicator after a few seconds
      setTimeout(() => setLastExported(null), 3000);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 ${className}`}
          disabled={exporting !== null}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Export detailed report
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {EXPORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isExporting = exporting === option.format;
          const wasExported = lastExported === option.format;

          return (
            <DropdownMenuItem
              key={option.format}
              onClick={() => handleExport(option.format)}
              disabled={isExporting}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-3 w-full">
                <div className="p-1.5 rounded bg-muted">
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : wasExported ? (
                    <Check className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {option.description}
                  </div>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AdvancedExportMenu;
